import { fitRynReplayBatchesForIngest } from './ryn-replay-batch-fit';
import { IRynReplayEnvelope } from './ryn-replay-batcher';
import { IRynReplayOfflineStore } from './ryn-replay-offline-store';
import { trimRynReplayRetryQueue } from './ryn-replay-recorder.retry-queue';
import { IRynReplayTransport } from './ryn-replay-transport';

/**
 * Getting a batch to ingest, and keeping it when that fails.
 *
 * Every rule in here is a rule about not losing a session, and every one of
 * them was arrived at once already on the web side. They are shared rather than
 * reimplemented per platform because a second copy is a copy that drifts: the
 * mobile backend had this logic verbatim, and a divergence in (say) whether a
 * failed batch goes to the offline store OR the retry queue rather than both is
 * invisible until someone notices sessions replaying twice.
 *
 * The platform-specific part is NOT here: which store backs `offlineStore`
 * (IndexedDB vs a file queue) and what "unloading" means (`pagehide` vs
 * `suspendEvent`) belong to the caller.
 */

/**
 * How many failed batches to hold in memory when there is no offline store.
 *
 * Bounded because a persistently failing endpoint must not grow this without
 * limit and take the tab's or the device's memory with it.
 */
export const RYN_REPLAY_MAX_RETRY_BATCHES = 4;

export class RynReplayDelivery {
  /**
   * Failed batches awaiting the next flush, used only when no offline store is
   * available. Eviction is NOT plain FIFO — `trimRynReplayRetryQueue` refuses
   * to drop a batch holding the session keyframe while any non-keyframe batch
   * remains, because losing the keyframe makes every later delta unplayable.
   */
  private retryQueue: IRynReplayEnvelope[][] = [];
  private draining = false;

  constructor(
    private readonly transport: IRynReplayTransport,
    private readonly offlineStore?: IRynReplayOfflineStore,
    private readonly maxRetryBatches: number = RYN_REPLAY_MAX_RETRY_BATCHES
  ) {}

  /** Take the failed batches, leaving the queue empty. */
  takePending(): IRynReplayEnvelope[][] {
    const pending = this.retryQueue;
    this.retryQueue = [];
    return pending;
  }

  /**
   * Send one batch, slimming and splitting it first so a dense keyframe cannot
   * 413 forever against the server's decompressed-size cap. Chunks retry
   * independently.
   *
   * A failed chunk goes to the offline store when there is one, and to the
   * in-memory queue otherwise — never BOTH. Keeping it in both would double-send
   * on the next flush-plus-drain and break replay ordering. The enqueue is
   * awaited so an overlapping drain cannot ship ahead of the batch that just
   * failed.
   */
  async send(
    batch: IRynReplayEnvelope[],
    options: { sessionId?: string; unloading?: boolean } = {}
  ): Promise<void> {
    if (batch.length === 0) {
      return;
    }

    for (const chunk of fitRynReplayBatchesForIngest(batch)) {
      let accepted = false;
      try {
        accepted = await this.transport.send(chunk, {
          keepalive: options.unloading,
        });
      } catch {
        accepted = false;
      }
      if (accepted) {
        continue;
      }

      if (options.sessionId && this.offlineStore) {
        await this.offlineStore.enqueue(options.sessionId, chunk);
      } else {
        this.retryQueue.push(chunk);
        trimRynReplayRetryQueue(this.retryQueue, this.maxRetryBatches);
      }
    }
  }

  /**
   * Ship persisted batches once delivery looks possible again.
   *
   * Oldest-first, and it STOPS at the first failure rather than continuing:
   * later batches are newer, so pushing past a failure would deliver a session
   * out of order. Re-entrancy is guarded because both a flush tick and a
   * connectivity/resume signal can invite a drain at the same moment, and two
   * concurrent drains would send the same batches twice.
   */
  async drainOffline(): Promise<void> {
    if (!this.offlineStore || this.draining) {
      return;
    }
    this.draining = true;
    try {
      for (;;) {
        // Small peeks rather than one big read, so a large backlog drains
        // oldest-first without loading every row into memory at once.
        const pending = await this.offlineStore.peekOldest(
          this.maxRetryBatches
        );
        if (pending.length === 0) {
          return;
        }
        for (const record of pending) {
          let accepted = false;
          try {
            accepted = await this.transport.send(record.batch);
          } catch {
            accepted = false;
          }
          if (!accepted) {
            return;
          }
          await this.offlineStore.remove(record.id);
        }
      }
    } finally {
      this.draining = false;
    }
  }
}
